import { useProductSearchQuery } from '@pco/api/generated/graphql';
import { useFlatCategories } from '@pco/api/mesh/categories/useCategories';
import type {
	Category,
	CategoryList,
	ProductSearchFacets,
	SortBySelection,
} from '@pco/modules/CategoriesNavigation/index.types';
import {
	createSearchQueryFilters,
	createSortByForSearchQuery,
	transformCategories,
} from '@pco/modules/CategoriesNavigation/utils/categoryNavigation';
import { CATEGORY_URL_KEY } from '@pco/modules/CategoriesNavigation/utils/constants.impl';
import type { Category as QueryCategory } from '@pco/modules/PrimaryNav/index.types';
import type {
	PartialParamVehicle,
	Vehicle,
} from '@pco/modules/VehicleSelection';
import { useControlledQueryParams } from '@pco/utils/useControlledQueryParam';
import { useMemo } from 'react';

const createCategoriesFromFacets = (
	facets: ProductSearchFacets,
): CategoryList => {
	const categoryList =
		facets
			?.find((facet) => facet?.attribute === 'categories')
			?.buckets.filter((bucket) => bucket?.__typename === 'CategoryView')
			.map((bucket) => {
				if (bucket?.title === '') return;
				const categoryLevels = bucket?.title.split('/') || [];
				if (categoryLevels?.length < 2) return;
				if (bucket?.__typename === 'CategoryView') {
					return {
						url_key: bucket.urlKey || '',
						url_path: bucket.title || '',
						name: bucket.name || '',
					};
				}
			})
			.filter(Boolean) || [];
	const uniqueCategoryList = [
		{
			url_path: '',
			name: 'All',
		},
		...new Map(categoryList.map((cat) => [cat.name, cat])).values(),
	];
	return uniqueCategoryList;
};

type useSearchCategoryNavigationProps = {
	searchTerm: string;
	pageSize: number;
	pageNumber: number;
	enabled: boolean;
	sortBySelection: SortBySelection;
	setPage: (pageNumber: number) => void;
	selectedVehicle?: Vehicle;
	paramVehicle?: PartialParamVehicle;
};
export const useSearchCategoryNavigation = ({
	searchTerm,
	sortBySelection,
	pageNumber,
	pageSize,
	setPage,
	selectedVehicle,
	enabled,
	paramVehicle,
}: useSearchCategoryNavigationProps) => {
	const [activeUrlParamCategory, setActiveUrlParamCategory] =
		useControlledQueryParams(CATEGORY_URL_KEY, '');
	const { isFetching: isCategoriesFetching, data: searchCategoriesProducts } =
		useProductSearchQuery(
			{
				phrase: searchTerm,
				filter: createSearchQueryFilters({
					selectedVehicle: selectedVehicle,
					paramVehicle,
					hasSearchTerm: true,
				}),
				sort: createSortByForSearchQuery({
					userSelectedSortBy: [sortBySelection],
					selectedVehicle,
				}),
				pageSize: pageSize,
				page: Number(pageNumber) || 1,
			},
			{
				enabled,
			},
		);
	const { isFetching: isSearchFetching, data: searchProductsData } =
		useProductSearchQuery(
			{
				phrase: searchTerm,
				filter: createSearchQueryFilters({
					selectedCategory: activeUrlParamCategory,
					selectedVehicle,
					paramVehicle,
					hasSearchTerm: true,
				}),
				sort: createSortByForSearchQuery({
					userSelectedSortBy: [sortBySelection],
					selectedVehicle,
				}),
				pageSize: pageSize,
				page: Number(pageNumber) || 1,
			},
			{
				gcTime: 5000,
				staleTime: 5000,
				placeholderData: (prev) => prev,
				enabled: !isCategoriesFetching && enabled,
			},
		);
	const { data: allCats } = useFlatCategories();
	const searchCategories: Category[] | undefined = useMemo(() => {
		const uniqueCategoryList = createCategoriesFromFacets(
			searchCategoriesProducts?.productSearch.facets,
		);
		const newSearchCategories =
			transformCategories(
				uniqueCategoryList,
				activeUrlParamCategory,
				'/content/category.html',
				setActiveUrlParamCategory,
				setPage,
			) || ([] as Category[]);
		const [allCategory, ...restCategories] = newSearchCategories;
		return [
			allCategory,
			...restCategories.filter(
				(category) =>
					allCats?.find(
						(allCat: QueryCategory) => allCat.url_key === category.urlKey,
					)?.include_in_search_filters ?? false,
			),
		];
	}, [
		searchCategoriesProducts,
		activeUrlParamCategory,
		setActiveUrlParamCategory,
		setPage,
		allCats,
	]);
	return {
		isLoading: isSearchFetching || isCategoriesFetching,
		searchCategories,
		searchProductsData,
	};
};
